import type { Lang } from './enums.ts';

const NBSP = ' ';

function group(amount: number, separator: string): string {
  return Math.trunc(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/**
 * Formats a UZS amount: `7 900 soʻm` / `7 900 сум` / `7,900 UZS`.
 * Uzbek uses the proper modifier letter ʻ (U+02BB), not an ASCII apostrophe.
 * Implemented by hand (not Intl) so the output is identical in every WebView.
 * Uses non-breaking spaces so a price never wraps.
 */
export function formatPrice(amountUzs: number, lang: Lang): string {
  if (!Number.isInteger(amountUzs)) throw new Error('UZS amounts must be integers');
  switch (lang) {
    case 'uz':
      return `${group(amountUzs, NBSP)}${NBSP}soʻm`;
    case 'ru':
      return `${group(amountUzs, NBSP)}${NBSP}сум`;
    case 'en':
      return `${group(amountUzs, ',')}${NBSP}UZS`;
  }
}

/** Formats seconds as `m:ss` (or `h:mm:ss`). */
export function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

const MONTHS: Record<Lang, readonly string[]> = {
  uz: [
    'yanvar',
    'fevral',
    'mart',
    'aprel',
    'may',
    'iyun',
    'iyul',
    'avgust',
    'sentabr',
    'oktabr',
    'noyabr',
    'dekabr',
  ],
  ru: [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

/**
 * Day and month in Tashkent time, in the form the copy expects:
 * uz `17-noyabr` (suffixes like -gacha/-da are in the message), ru `17 ноября`, en `November 17`.
 */
export function formatDayMonth(date: Date, lang: Lang): string {
  const local = new Date(date.getTime() + 5 * 60 * 60_000);
  const day = local.getUTCDate();
  const month = MONTHS[lang][local.getUTCMonth()] ?? '';
  if (lang === 'uz') return `${day}-${month}`;
  if (lang === 'ru') return `${day} ${month}`;
  return `${month} ${day}`;
}

/** Story length for lists: whole minutes, at least 1. */
export function durationMinutes(totalSec: number | null): number {
  return Math.max(1, Math.round((totalSec ?? 0) / 60));
}

/** "12 400 so'm per month" for yearly plans: monthly price rounded down to 100 so'm. */
export function monthlyPrice(priceUzs: number, days: number): number {
  return Math.floor(priceUzs / (days / 30.4375) / 100) * 100;
}

/** Like formatDayMonth, with the year: `17-oktabr 2027` / `17 октября 2027` / `October 17, 2027`. */
export function formatDayMonthYear(date: Date, lang: Lang): string {
  const year = new Date(date.getTime() + 5 * 60 * 60_000).getUTCFullYear();
  const dm = formatDayMonth(date, lang);
  return lang === 'en' ? `${dm}, ${year}` : `${dm} ${year}`;
}

/**
 * Whole-percent saving of a long plan versus paying the monthly price for the same period
 * (paywall badge "38% tejamkor"). Computed from DB prices, never hardcoded. 0 when not cheaper.
 */
export function savingsPercent(
  plan: { priceUzs: number; days: number },
  monthly: { priceUzs: number; days: number },
): number {
  const sameAsMonthly = (monthly.priceUzs / monthly.days) * plan.days;
  if (sameAsMonthly <= 0) return 0;
  return Math.max(0, Math.round((1 - plan.priceUzs / sameAsMonthly) * 100));
}
